import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import {
  BookOpen,
  CalendarClock,
  ChevronDown,
  HandCoins,
  Kanban as KanbanIcon,
  KanbanSquare,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  Moon,
  Plus,
  Repeat,
  Sparkles,
  StickyNote,
  Sun,
  UserCog,
  Users,
  Wallet,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { LeadDrawerProvider } from "@/lib/leadDrawer";
import { LeadDrawer } from "@/components/leads/LeadDrawer";
import { NovoLeadModal } from "@/components/leads/NovoLeadModal";

// Nav agrupada por secao do Admin. "Financeiro" so aparece para socios
// (app_users.socio) - o mesmo corte que a RLS ja aplica nos dados
// (is_socio(), ver 20260911150000_v13_financeiro.sql); esconder o link evita
// levar quem nao tem acesso a uma tela que so mostraria listas vazias.
const CHAVE_SECOES = "cafelabs-admin:secoes-recolhidas";

const SECAO_CLIENTES = {
  label: "Clientes",
  links: [
    { to: "/clientes", label: "Fila de hoje", end: true, icon: ListChecks },
    { to: "/clientes/kanban", label: "Kanban", end: false, icon: KanbanIcon },
    { to: "/clientes/leads", label: "Leads", end: false, icon: Users },
    { to: "/clientes/fechados", label: "Clientes fechados", end: false, icon: HandCoins },
    { to: "/clientes/scripts", label: "Scripts", end: false, icon: BookOpen },
    { to: "/clientes/dashboard", label: "Dashboard", end: false, icon: LayoutDashboard },
  ],
};

const SECAO_ORGANIZACAO = {
  label: "Organização",
  links: [
    { to: "/organizacao/tarefas", label: "Tarefas da equipe", end: false, icon: KanbanSquare },
    { to: "/organizacao/reunioes", label: "Agenda de reuniões", end: false, icon: CalendarClock },
    { to: "/organizacao/rotinas", label: "Checklists recorrentes", end: false, icon: Repeat },
    { to: "/organizacao/notas", label: "Notas e ideias", end: false, icon: StickyNote },
  ],
};

const SECAO_FINANCEIRO = {
  label: "Financeiro",
  links: [{ to: "/financeiro", label: "Recebimentos e despesas", end: false, icon: Wallet }],
};

const SECAO_PRODUTOS = {
  label: "Café Labs",
  links: [{ to: "/produtos", label: "Produtos", end: false, icon: Sparkles }],
};

const SECAO_ADMINISTRACAO = {
  label: "Administração",
  links: [{ to: "/admin/usuarios", label: "Usuários", end: false, icon: UserCog }],
};

export function AppShell() {
  const { appUser, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [novoLeadAberto, setNovoLeadAberto] = useState(false);
  // Secoes recolhidas ficam guardadas por navegador (conveniencia por
  // pessoa, nao dado do sistema) — localStorage pode falhar/bloquear.
  const [secoesRecolhidas, setSecoesRecolhidas] = useState<string[]>(() => {
    try {
      const salvo = JSON.parse(localStorage.getItem(CHAVE_SECOES) ?? "[]");
      return Array.isArray(salvo) ? salvo.filter((s): s is string => typeof s === "string") : [];
    } catch {
      return [];
    }
  });
  function alternarSecao(label: string) {
    setSecoesRecolhidas((atual) => {
      const nova = atual.includes(label) ? atual.filter((s) => s !== label) : [...atual, label];
      try {
        localStorage.setItem(CHAVE_SECOES, JSON.stringify(nova));
      } catch {
        // sem storage: so nao lembra na proxima visita
      }
      return nova;
    });
  }
  // "Novo lead" e uma acao da secao Clientes, nao do Admin como um todo —
  // esconder fora dela evita ficar orfa quando outras secoes (ex.: Financeiro)
  // existirem.
  const emSecaoClientes = location.pathname.startsWith("/clientes");
  const navSections = appUser?.socio
    ? [SECAO_CLIENTES, SECAO_ORGANIZACAO, SECAO_FINANCEIRO, SECAO_PRODUTOS, SECAO_ADMINISTRACAO]
    : [SECAO_CLIENTES, SECAO_ORGANIZACAO, SECAO_PRODUTOS];

  return (
    <LeadDrawerProvider>
      <a href="#conteudo-principal" className="skip-link">
        Pular para o conteúdo
      </a>
      <div className="app-shell">
        {/* Backdrop fecha no clique apenas como atalho de mouse: o
            equivalente por teclado e o proprio botao de menu (aria-expanded)
            na topbar, ja focavel e operavel por Enter/Espaco. */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          className={`sidebar-backdrop${sidebarAberta ? " is-open" : ""}`}
          onClick={() => setSidebarAberta(false)}
        />
        <nav
          className={`sidebar${sidebarAberta ? " is-open" : ""}`}
          aria-label="Navegação principal"
        >
          <NavLink to="/" end className="sidebar__brand" onClick={() => setSidebarAberta(false)}>
            <img src="/logos/cafelabs.svg" alt="" width={32} height={32} className="icon" />
            Café Labs Admin
          </NavLink>
          <div className="sidebar__nav">
            {navSections.map((section) => {
              const recolhida = secoesRecolhidas.includes(section.label);
              const temPaginaAtiva = section.links.some((l) =>
                l.end ? location.pathname === l.to : location.pathname.startsWith(l.to),
              );
              const listaId = `secao-${section.label}`;
              return (
                <div key={section.label} className="sidebar__section">
                  <button
                    type="button"
                    className="sidebar__section-label"
                    aria-expanded={!recolhida}
                    aria-controls={listaId}
                    onClick={() => alternarSecao(section.label)}
                  >
                    {section.label}
                    {recolhida && temPaginaAtiva && (
                      <span className="sidebar__section-dot" aria-label="contém a página atual" />
                    )}
                    <ChevronDown className="icon sidebar__section-chevron" size={16} aria-hidden="true" />
                  </button>
                  {!recolhida && (
                    <div id={listaId} className="sidebar__section-links">
                      {section.links.map((link) => (
                        <NavLink
                          key={link.to}
                          to={link.to}
                          end={link.end}
                          className="sidebar__link"
                          onClick={() => setSidebarAberta(false)}
                        >
                          <link.icon className="icon" size={18} aria-hidden="true" />
                          {link.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="sidebar__footer">
            <button
              type="button"
              className="theme-toggle"
              onClick={toggleTheme}
              aria-pressed={theme === "dark"}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {theme === "dark" ? (
                  <Moon className="icon" size={16} aria-hidden="true" />
                ) : (
                  <Sun className="icon" size={16} aria-hidden="true" />
                )}
                Tema {theme === "dark" ? "escuro" : "claro"}
              </span>
            </button>
            <div className="sidebar__user">{appUser?.nome}</div>
            <button type="button" className="sidebar__signout" onClick={() => void signOut()}>
              <LogOut className="icon" size={16} aria-hidden="true" />
              Sair
            </button>
          </div>
        </nav>

        <div className="app-main">
          <header className="topbar">
            <button
              type="button"
              className="btn btn--ghost btn--icon mobile-nav-toggle"
              aria-label="Abrir menu de navegação"
              aria-expanded={sidebarAberta}
              onClick={() => setSidebarAberta((v) => !v)}
            >
              <Menu className="icon" size={20} aria-hidden="true" />
            </button>
            <div className="spacer" />
            {emSecaoClientes && (
              <button type="button" className="btn btn--primary" onClick={() => setNovoLeadAberto(true)}>
                <Plus className="icon" size={16} aria-hidden="true" />
                Novo lead
              </button>
            )}
          </header>

          <main id="conteudo-principal" className="page-content">
            <Outlet />
          </main>
        </div>
      </div>

      <LeadDrawer />
      <AnimatePresence>
        {novoLeadAberto && <NovoLeadModal key="novo-lead" onClose={() => setNovoLeadAberto(false)} />}
      </AnimatePresence>
    </LeadDrawerProvider>
  );
}
